import {
  clearStandaloneUser,
  isStandaloneUser as getIsStandaloneUser,
} from "../utils/standaloneUser";
import { clearSessionToken } from "../lib/sessionToken";

/**
 * Provides the shared logout routine: session cleanup, Pylon reset, and
 * redirect. Used by the sidebar and LogoutButton.
 *
 * The redirect is a full page load so a recording hits the browser leave
 * prompt. Leave unloads the page and finishes logout. Stay cancels the
 * load, so the session and the recording stay as they were.
 *
 * Cleanup runs on pagehide, after the browser has committed to leaving.
 * A toast or a logging request started there is discarded with the page.
 * If the clinician stays, that pagehide listener is removed. Otherwise a
 * later, unrelated exit would clear a session they chose to keep.
 */
export function useLogout(setAuth) {
  return () => {
    const destination = getIsStandaloneUser() ? "/standalone" : "/login";
    let loggedOut = false;
    let unloadEvent = null;

    const detachCancelledLogout = () => {
      window.removeEventListener("pagehide", onPageHide, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.navigation?.removeEventListener?.("navigate", onNavigate);
    };

    const finishLogout = () => {
      if (loggedOut) {
        return;
      }
      loggedOut = true;

      clearSessionToken();
      localStorage.removeItem("date");
      clearStandaloneUser();
      window.pylon = {};
      if (typeof setAuth === "function") {
        setAuth(false);
      }
      window.navigation?.removeEventListener?.("navigate", onNavigate);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };

    const onPageHide = () => {
      window.removeEventListener("pagehide", onPageHide, true);
      finishLogout();
    };

    const onBeforeUnload = (event) => {
      unloadEvent = event;
    };

    const onNavigate = (event) => {
      let pathname = "";
      try {
        pathname = new URL(event.destination.url).pathname;
      } catch {
        return;
      }
      if (pathname !== destination) {
        return;
      }

      event.signal.addEventListener(
        "abort",
        () => {
          if (loggedOut) {
            return;
          }
          detachCancelledLogout();
        },
        { once: true }
      );
    };

    const navigation = window.navigation;
    const canListenForNavigate =
      navigation != null && typeof navigation.addEventListener === "function";

    window.addEventListener("pagehide", onPageHide, true);
    if (canListenForNavigate) {
      navigation.addEventListener("navigate", onNavigate);
    } else {
      // No Navigation API abort signal. The leave prompt cancels assign()
      // and leaves this document running, with defaultPrevented set.
      window.addEventListener("beforeunload", onBeforeUnload);
    }

    window.location.assign(destination);

    if (
      !canListenForNavigate &&
      !loggedOut &&
      unloadEvent?.defaultPrevented &&
      document.visibilityState !== "hidden"
    ) {
      detachCancelledLogout();
    }
  };
}

export default useLogout;
